/**
 * How completely a process step is mapped (z8nrz7cra6).
 *
 * The Process page's status axis. It replaces the binary Configured / Not
 * configured filter (z8nrz7cr9r) with the three levels the ticket asks for,
 * because "configured" answered the wrong question: a step with one staffing
 * line and nothing else was "Configured" and therefore indistinguishable from a
 * step that was actually finished, which is the state anyone auditing a form
 * wants to find.
 *
 * **The dimensions counted are equipment, inventory and staffing** — the three a
 * process step can actually be given, and the three the builder has editors for.
 *
 * `attributes` is deliberately NOT one of them, even though the table shows a
 * column for it and the Field Attributes editor now authors the set (starting
 * with a step's method). Adding it here is still a one-line change — the
 * dimension list is a constant for exactly that reason — but it is HELD, on
 * purpose: every process step with no attributes set would drop out of
 * `fully-mapped` the moment it landed, reclassifying every row in every tenant.
 * That is a decision about what the filter means, not a code change, and it is
 * the process owner's to make.
 *
 * **Known limitation, recorded rather than hidden:** a step that legitimately
 * needs no materials — an incubation, say, which occupies a machine and a person
 * and consumes nothing — cannot reach `fully-mapped`. It sits at
 * `partially-mapped` forever. Distinguishing "no materials needed" from "no
 * materials entered yet" needs an explicit "nothing required here" mark on the
 * step, which does not exist. Until it does, `partially-mapped` means "not all
 * three", not "unfinished".
 */
export type MappingLevel = 'not-mapped' | 'partially-mapped' | 'fully-mapped'

/** The child-row counts `GET /process-steps` returns for one connection. */
export interface MappableCounts {
  equipmentRequirements: number
  inventoryRequirements: number
  staffingLines: number
}

/**
 * The dimensions mapping is measured over, in the order the table shows them.
 *
 * Exported so the count of them is derived rather than restated: a page saying
 * "2 of 3 mapped" reads its denominator from here.
 */
export const MAPPING_DIMENSIONS: Array<keyof MappableCounts> = [
  'equipmentRequirements',
  'inventoryRequirements',
  'staffingLines',
]

/** Human labels, so the picker, the badge and the caption cannot disagree. */
export const MAPPING_LABELS: Record<MappingLevel, string> = {
  'not-mapped': 'Not Mapped',
  'partially-mapped': 'Partially Mapped',
  'fully-mapped': 'Fully Mapped',
}

/**
 * How many of the three dimensions hold at least one row.
 *
 * A dimension is mapped or it is not — five equipment requirements are no more
 * mapped than one. The question this filter answers is which parts of the step
 * have been thought about, not how big they are.
 */
export function mappedDimensionCount(counts: MappableCounts | undefined): number {
  if (!counts) return 0
  return MAPPING_DIMENSIONS.filter((key) => counts[key] > 0).length
}

/**
 * A process's mapping level.
 *
 * `undefined` counts — a process field with no `ProcessStepConnection` at all —
 * is `not-mapped`, not a fourth state. It was placed on a canvas and never set
 * up, which is precisely what the level means, and the row it produces is a row
 * of zeroes either way.
 */
export function processMappingLevel(counts: MappableCounts | undefined): MappingLevel {
  const mapped = mappedDimensionCount(counts)
  if (mapped === 0) return 'not-mapped'
  return mapped === MAPPING_DIMENSIONS.length ? 'fully-mapped' : 'partially-mapped'
}
